import { esc } from "../ui/format.js";

/**
 * What needs you (GET /api/attention, backend/attention.py).
 *
 *  - a red count badge on every sidebar tab where something is waiting (a
 *    letter, a payment due, an offer, a live moment), and on the phone's
 *    bottom-bar group that holds it;
 *  - an alert banner at the top of that section saying what is waiting. Each
 *    line is a link (Rachit, 2026-10-24): tapping it scrolls to the exact spot
 *    on the page it's about (the item's `focus` selector from attention.py, or
 *    the heading whose words match it best), opens any folded panel around it
 *    and flashes it. "office:inbox" style focuses open a CEO Office page.
 *
 * Refreshed after every refreshAll (so a badge clears once it's answered).
 */

let attTabs = {};

function attBadge(n, urgent) {
  return `<span class="att-badge${urgent ? " urgent" : ""}" aria-label="${n} waiting">${n > 9 ? "9+" : n}</span>`;
}

function attPaintSidebar() {
  document.querySelectorAll(".sidebar .tab, .mobile-sheet-item").forEach((b) => {
    b.querySelectorAll(".att-badge").forEach((x) => x.remove());
    const t = attTabs[b.dataset.tab];
    b.classList.toggle("att-has", !!t);
    if (!t) { b.removeAttribute("data-att"); return; }
    b.insertAdjacentHTML("beforeend", attBadge(t.count, t.urgent));
    b.dataset.att = t.why.join(" · ");
    if (!b.classList.contains("locked")) b.title = t.why.join("\n");
  });
  // the phone's bottom bar: one dot per group with anything waiting
  const groups = typeof window.ges === "object" && window.ges.mobileNavGroups ? window.ges.mobileNavGroups() : null;
  document.querySelectorAll("#mobile-nav .mobile-nav-btn").forEach((b) => {
    b.querySelectorAll(".att-badge").forEach((x) => x.remove());
    const g = groups && groups[+b.dataset.group];
    const n = g ? g.tabs.reduce((s, t) => s + ((attTabs[t.tab] || {}).count || 0), 0) : 0;
    const urgent = g ? g.tabs.some((t) => (attTabs[t.tab] || {}).urgent) : false; // red only when something must be answered
    if (n) b.insertAdjacentHTML("beforeend", attBadge(n, urgent));
  });
}

function attBanner() {
  const main = document.getElementById("main-content");
  // each render is a .tab-view that names its tab (app/shell.js renderTab)
  const host = main && main.firstElementChild;
  if (!host || !host.dataset.tab) return;
  host.querySelectorAll(":scope > .att-banner").forEach((x) => x.remove());
  const t = attTabs[host.dataset.tab];
  if (!t) return;
  const items = t.items || t.why.map((w) => ({ text: w }));
  host.insertAdjacentHTML("afterbegin", `<div class="att-banner${t.urgent ? " urgent" : ""}" role="status">
    <span class="att-banner-icon" aria-hidden="true">🔔</span>
    <div><b>Needs your attention</b> <span class="att-count${t.urgent ? " urgent" : ""}">${t.count}</span> <span class="att-hint">tap a line to go there</span><ul>${items.map((it, i) =>
      `<li><button type="button" class="att-go" data-att-i="${i}">${esc(it.text)} <span aria-hidden="true">→</span></button></li>`).join("")}${t.more ? `<li class="att-more">and ${t.more} more</li>` : ""}</ul></div></div>`);
  host.querySelectorAll(":scope > .att-banner [data-att-i]").forEach((b) => b.addEventListener("click", () => attGo(host.dataset.tab, items[+b.dataset.attI])));
}

const ATT_STOP = new Set(["a", "an", "the", "to", "of", "in", "on", "for", "your", "you", "is", "are", "and", "or", "it", "its",
  "has", "have", "with", "at", "by", "be", "this", "that", "waits", "wait", "needs", "need", "answer", "from", "more"]);

function attWords(text) {
  return (text || "").toLowerCase().replace(/[^a-z0-9 ]+/g, " ").split(/\s+/).filter((w) => w.length > 2 && !ATT_STOP.has(w));
}

/** The element on this tab that a line is about: its selector, else the heading that shares the most words. */
function attFind(host, item) {
  if (item.focus && !item.focus.includes(":")) {
    try { const el = host.querySelector(item.focus); if (el) return el; } catch (e) { /* not a selector */ }
  }
  const want = attWords(item.text);
  if (!want.length) return null;
  let best = null, bestScore = 0;
  host.querySelectorAll("h2, h3, h4, summary, .sx-card-title, .doc-subject, .th-item b, .desk-item b, .em-moves-t, .mv-card h4, .eng-verdict, .alert, p.sx-card-sub")
    .forEach((el) => {
      if (el.closest(".att-banner")) return;
      const have = new Set(attWords(el.textContent));
      const score = want.reduce((n, w) => n + (have.has(w) ? 1 : 0), 0) / Math.sqrt(have.size + 1);
      if (score > bestScore) { bestScore = score; best = el; }
    });
  return bestScore > 0 ? best.closest("article, section, details, .mv-card, .th-item, .desk-item") || best : null;
}

function attFlash(el) {
  const pane = el.closest("[data-dl-pane]"); // a line about a Deals sub-tab that isn't showing: open that sub-tab first
  if (pane && pane.hidden) { const t = document.querySelector(`[data-dl-tab="${pane.dataset.dlPane}"]`); if (t) t.click(); }
  for (let d = el.closest("details"); d; d = d.parentElement && d.parentElement.closest("details")) d.open = true;
  if (el.tagName === "DETAILS") el.open = true;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.classList.remove("att-flash");
  void el.offsetWidth;
  el.classList.add("att-flash");
  setTimeout(() => el.classList.remove("att-flash"), 2400);
}

async function attGo(tab, item) {
  if (!item) return;
  if ((item.focus || "").startsWith("call:")) {  // a decision with its own pop-up (the quarter's dividend)
    const fn = window[item.focus.slice(5)];
    if (typeof fn === "function") fn();
    return;
  }
  const [ftab, page] = (item.focus || "").includes(":") ? item.focus.split(":") : [null, null];
  if (ftab && page) {  // a page inside a tab (CEO Office: Inbox, Moments)
    try { localStorage.setItem(`ges.${ftab}.tab`, page); } catch (e) { /* not remembered */ }
    if (window.ges && window.ges.showTab) await window.ges.showTab(ftab);
    return;
  }
  const main = document.getElementById("main-content");
  const host = main && main.firstElementChild;
  // panels (letters, engineers...) load a moment after the tab: look again until they're there
  for (let k = 0; k < 12; k += 1) {
    const el = host && attFind(host, item);
    if (el) { attFlash(el); if (el.matches(".th-item, .desk-item")) el.click(); return; }
    await new Promise((r) => setTimeout(r, 250));
  }
}

async function refreshAttention() {
  try {
    const r = await fetch("/api/attention");
    if (!r.ok) return;
    attTabs = (await r.json()).tabs || {};
  } catch (e) { return; }
  attPaintSidebar();
  attBanner();
}

if (typeof window !== "undefined" && typeof document !== "undefined") {
  const original = window.refreshAll;
  if (typeof original === "function") {
    window.refreshAll = async function (...args) {
      const out = await original.apply(this, args);
      refreshAttention();
      return out;
    };
  }
  // a new tab render replaces the content: put that section's banner back on top
  const watch = () => {
    const main = document.getElementById("main-content");
    if (!main || main.dataset.attWatched) return;
    main.dataset.attWatched = "1";
    new MutationObserver(() => attBanner()).observe(main, { childList: true });
  };
  document.addEventListener("click", (e) => {
    watch();
    if (e.target.closest("#mobile-nav .mobile-nav-btn")) setTimeout(attPaintSidebar, 0);
  });
  watch();
  window.refreshAttention = refreshAttention;
  window.gesFlash = attFlash; // the first-year guide's "Show me" uses the same highlight
}

export { attFind, refreshAttention };
